// Shared fee/tax calculation utility
// Reads event-level fee config and computes fees dynamically

export interface EventFeeConfig {
  taxRate: number           // e.g. 0.08 for 8%
  serviceFeeFixed: number | null    // e.g. 2.50
  serviceFeePercentage: number | null // e.g. 5 for 5%
  serviceFeeMax: number | null
  serviceFeeMin: number | null
}

export interface FeeResult {
  serviceFee: number
  tax: number
}

// Default fee config — all zeros unless explicitly configured on the event
const DEFAULT_FEE_CONFIG: EventFeeConfig = {
  taxRate: 0,
  serviceFeeFixed: 0,
  serviceFeePercentage: 0,
  serviceFeeMax: null,
  serviceFeeMin: null,
}

/**
 * Calculate fees and tax for a given subtotal using event-level fee configuration.
 * Defaults to zero fees/tax — only charges what's explicitly set on the event.
 */
export function calculateFees(subtotal: number, config?: Partial<EventFeeConfig> | null): FeeResult {
  if (subtotal <= 0) {
    return { serviceFee: 0, tax: 0 }
  }

  const cfg: EventFeeConfig = {
    taxRate: config?.taxRate ?? DEFAULT_FEE_CONFIG.taxRate,
    serviceFeeFixed: config?.serviceFeeFixed ?? DEFAULT_FEE_CONFIG.serviceFeeFixed,
    serviceFeePercentage: config?.serviceFeePercentage ?? DEFAULT_FEE_CONFIG.serviceFeePercentage,
    serviceFeeMax: config?.serviceFeeMax ?? DEFAULT_FEE_CONFIG.serviceFeeMax,
    serviceFeeMin: config?.serviceFeeMin ?? DEFAULT_FEE_CONFIG.serviceFeeMin,
  }

  // Calculate service fee: percentage component + fixed component
  let serviceFee = 0
  if (cfg.serviceFeePercentage != null && cfg.serviceFeePercentage > 0) {
    serviceFee += subtotal * (cfg.serviceFeePercentage / 100)
  }
  if (cfg.serviceFeeFixed != null && cfg.serviceFeeFixed > 0) {
    serviceFee += cfg.serviceFeeFixed
  }

  // Apply min/max bounds
  if (cfg.serviceFeeMin != null && serviceFee < cfg.serviceFeeMin) {
    serviceFee = cfg.serviceFeeMin
  }
  if (cfg.serviceFeeMax != null && serviceFee > cfg.serviceFeeMax) {
    serviceFee = cfg.serviceFeeMax
  }

  // Calculate tax
  const tax = subtotal * cfg.taxRate

  return {
    serviceFee: Math.round(serviceFee * 100) / 100,
    tax: Math.round(tax * 100) / 100,
  }
}

/**
 * Extract EventFeeConfig from a Prisma event record.
 * Converts Decimal fields to plain numbers.
 */
export function extractFeeConfig(event: {
  taxRate?: unknown
  serviceFeeFixed?: unknown
  serviceFeePercentage?: unknown
  serviceFeeMax?: unknown
  serviceFeeMin?: unknown
}): EventFeeConfig {
  const toNum = (val: unknown): number | null => {
    if (val == null) return null
    if (typeof val === 'number') return val
    if (typeof val === 'object' && 'toNumber' in (val as object)) {
      return (val as { toNumber: () => number }).toNumber()
    }
    const n = Number(val)
    return isNaN(n) ? null : n
  }

  return {
    taxRate: toNum(event.taxRate) ?? DEFAULT_FEE_CONFIG.taxRate!,
    serviceFeeFixed: toNum(event.serviceFeeFixed),
    serviceFeePercentage: toNum(event.serviceFeePercentage),
    serviceFeeMax: toNum(event.serviceFeeMax),
    serviceFeeMin: toNum(event.serviceFeeMin),
  }
}

/**
 * Calculate the all-inclusive price for a single ticket (base + fees + tax).
 * Uses the simple fee system (taxRate + serviceFeeFixed + serviceFeePercentage).
 */
/**
 * The advertised price for ONE ticket: base plus every mandatory fee the
 * buyer cannot avoid, plus tax where the event charges it at the storefront.
 *
 * Fixed fees are included even though they are charged once per order rather
 * than per ticket. They used to be excluded as "per-order, not per-ticket",
 * which made the advertised price lower than the cheapest thing anyone could
 * actually buy: a $65 ticket with a 3% fee and a $1.50 fixed fee advertised
 * $66.95 and charged $68.45 at checkout. Advertising below the real total is
 * the exact thing all-in pricing rules prohibit, so the single-ticket total
 * is the floor. On a multi-ticket order the per-ticket average comes in
 * under this, which is the safe direction to be wrong in.
 */
export function calculateAllInPrice(unitPrice: number, config?: Partial<EventFeeConfig> | null): number {
  if (unitPrice <= 0) return 0
  const { serviceFee, tax } = calculateFees(unitPrice, config)
  return Math.round((unitPrice + serviceFee + tax) * 100) / 100
}

// ── Custom Fee System (EventFee model) ──────────────────────────────

export interface EventFeeItem {
  name: string
  type: 'fixed' | 'percentage'
  amount: number
  applyAtStorefront: boolean
  applyAtBoxOffice: boolean
  /** Fixed fees only: charge per ticket instead of once per order. */
  perTicket?: boolean
}

export type SalesChannel = 'storefront' | 'boxoffice'

export interface DetailedFeeResult {
  fees: Array<{ name: string; amount: number }>
  totalFees: number
  tax: number
}

/**
 * Calculate itemized fees and tax using EventFee records.
 * Filters fees by sales channel, computes each fee, and returns a breakdown.
 */
/**
 * How many tickets an order carries, for fixed fees marked `perTicket`.
 * Ticket lines only: add-ons, merch and donations do not produce a ticket
 * and must not each attract a per-ticket service charge.
 */
export function countTicketsForFees(
  items: Array<{
    quantity?: number | null
    ticketTypeId?: string | null
    venueAddonId?: string | null
    globalAddonId?: string | null
    shopifyItemId?: string | null
    ticketType?: { isDonation?: boolean | null } | null
    isDonation?: boolean | null
  }>,
): number {
  return items.reduce((sum, i) => {
    if (!i.ticketTypeId) return sum
    if (i.venueAddonId || i.globalAddonId || i.shopifyItemId) return sum
    if (i.ticketType?.isDonation || i.isDonation) return sum
    return sum + (i.quantity ?? 1)
  }, 0)
}

export function calculateDetailedFees(
  subtotal: number,
  feeItems: EventFeeItem[],
  taxRate: number,
  channel: SalesChannel,
  applyTax: boolean,
  /**
   * Tickets on the order, for fixed fees marked `perTicket`. Defaults to 1,
   * which is both the per-ticket display case and the historical per-order
   * behaviour, so a caller that does not pass it charges exactly what it
   * charged before.
   */
  ticketCount: number = 1,
): DetailedFeeResult {
  if (subtotal <= 0) {
    return { fees: [], totalFees: 0, tax: 0 }
  }

  const applicableFees = feeItems.filter((fee) =>
    channel === 'storefront' ? fee.applyAtStorefront : fee.applyAtBoxOffice,
  )

  const feeResults: Array<{ name: string; amount: number }> = []

  for (const fee of applicableFees) {
    let amount = 0
    if (fee.type === 'percentage') {
      // Percentages already scale with the order, so perTicket is moot.
      amount = subtotal * (fee.amount / 100)
    } else {
      amount = fee.perTicket ? fee.amount * Math.max(1, Math.floor(ticketCount)) : fee.amount
    }
    feeResults.push({ name: fee.name, amount: Math.round(amount * 100) / 100 })
  }

  const totalFees = feeResults.reduce((sum, f) => sum + f.amount, 0)
  const tax = applyTax ? Math.round(subtotal * taxRate * 100) / 100 : 0

  return {
    fees: feeResults,
    totalFees: Math.round(totalFees * 100) / 100,
    tax,
  }
}

/**
 * Calculate the all-inclusive price for a single ticket using the detailed fee system.
 * Excludes fixed fees since they are per-order, not per-ticket
 * (same logic as calculateAllInPrice which sets serviceFeeFixed: 0).
 */
/**
 * Same contract as calculateAllInPrice, for events using itemised EventFee
 * rows: the total a buyer pays for one ticket, fixed fees included. See that
 * function for why fixed fees count even though they are per-order.
 */
export function calculateAllInPriceDetailed(
  unitPrice: number,
  feeItems: EventFeeItem[],
  taxRate: number,
  applyTax: boolean,
): number {
  if (unitPrice <= 0) return 0
  const { totalFees, tax } = calculateDetailedFees(unitPrice, feeItems, taxRate, 'storefront', applyTax)
  return Math.round((unitPrice + totalFees + tax) * 100) / 100
}

/**
 * Everything needed to price a WHOLE ORDER the way checkout will charge it.
 *
 * Serialisable, so a server component can hand it to a client one - which is the
 * reason this exists. The storefront only received a per-ticket `allInPrice` and
 * reached the order total by multiplying it, and a fixed fee is per-ORDER unless
 * EventFee.perTicket is set. So three $59 tickets were advertised at $177 and
 * charged at $172, with both figures on the same checkout screen ("Your Tickets
 * 2 $118.00" above "Complete Order - $115.50").
 */
export interface AllInOrderPricing {
  hasDetailedFees: boolean
  feeConfig: Partial<EventFeeConfig> | null
  feeItems: EventFeeItem[]
  taxRate: number
  applyTax: boolean
}

/**
 * The all-in total for `ticketCount` tickets whose base prices sum to `subtotal`.
 *
 * Use this for any figure covering more than one ticket. calculateAllInPrice and
 * calculateAllInPriceDetailed stay as they are - they are the ADVERTISED
 * single-ticket price, which CLAUDE.md defines as the unit total including fixed
 * fees, and which is correct at a quantity of one. The bug was never those; it
 * was multiplying their result.
 */
export function calculateAllInOrderTotal(
  subtotal: number,
  ticketCount: number,
  pricing: AllInOrderPricing,
  channel: SalesChannel = 'storefront',
): number {
  if (subtotal <= 0) return 0
  const count = Math.max(1, Math.floor(ticketCount))
  if (pricing.hasDetailedFees) {
    const { totalFees, tax } = calculateDetailedFees(
      subtotal, pricing.feeItems, pricing.taxRate, channel, pricing.applyTax, count,
    )
    return finite(subtotal + totalFees + tax, subtotal, 'detailed')
  }
  /**
   * The simple config path has no per-ticket concept: its fixed fee is per order,
   * and its percentage already scales with the subtotal.
   *
   * calculateFees returns { serviceFee, tax } - NOT { totalFees, tax }, which is
   * what calculateDetailedFees returns. Destructuring totalFees here produced
   * `subtotal + undefined + tax` = NaN on EVERY event with no EventFee rows:
   * 34 of 60 live staging events and 463 of 517 on-sale production events would
   * have shown $NaN as their total. next.config has ignoreBuildErrors, so tsc
   * named this by line and the build still exited 0.
   */
  const { serviceFee, tax } = calculateFees(subtotal, pricing.feeConfig)
  return finite(subtotal + serviceFee + tax, subtotal, 'simple')
}

/**
 * Last resort on a money figure a buyer reads.
 *
 * It LOGS, loudly, and it understates - returning the bare subtotal, so the buyer
 * is quoted less than checkout will charge. Both of those are deliberate. The
 * silent version of this guard masked the very bug it was added for: with the
 * wrong field name restored, fourteen of fifteen tests still passed because NaN
 * was quietly swallowed into "subtotal". A guard that hides a defect is worse than
 * no guard, so this one shouts.
 *
 * It should never fire. The tests pin the arithmetic; this exists so that if it
 * ever does fire, the storefront still renders a number and somebody finds out.
 */
function finite(total: number, fallback: number, path: 'simple' | 'detailed'): number {
  if (Number.isFinite(total)) return Math.round(total * 100) / 100
  console.error(
    `[fee-calculation] ${path} path produced a non-finite total (${total}) for subtotal ${fallback}. ` +
    'Quoting the subtotal, which UNDERSTATES the price. This is a bug - fix the inputs.',
  )
  return Math.round(fallback * 100) / 100
}

/**
 * Convert Prisma EventFee records (with Decimal fields) to plain EventFeeItem[].
 */
export function extractEventFees(
  eventFees: Array<{
    name: string
    type: string
    amount: unknown
    applyAtStorefront: boolean
    applyAtBoxOffice: boolean
  }>,
): EventFeeItem[] {
  const toNum = (val: unknown): number => {
    if (val == null) return 0
    if (typeof val === 'number') return val
    if (typeof val === 'object' && 'toNumber' in (val as object)) {
      return (val as { toNumber: () => number }).toNumber()
    }
    const n = Number(val)
    return isNaN(n) ? 0 : n
  }

  return eventFees.map((f) => ({
    name: f.name,
    type: f.type as 'fixed' | 'percentage',
    amount: toNum(f.amount),
    perTicket: (f as { perTicket?: boolean }).perTicket === true,
    applyAtStorefront: f.applyAtStorefront,
    applyAtBoxOffice: f.applyAtBoxOffice,
  }))
}

/** The fee-bearing fields of an event, as any route that prices tickets loads them. */
export interface AllInPricingEvent {
  eventFees?: Array<{ name: string; type: string; amount: unknown; applyAtStorefront: boolean; applyAtBoxOffice: boolean }> | null
  taxRate?: unknown
  applyTaxAtStorefront?: boolean | null
  applyTaxAtBoxOffice?: boolean | null
  applyFeesAtBoxOffice?: boolean | null
  serviceFeeFixed?: unknown
  serviceFeePercentage?: unknown
  serviceFeeMax?: unknown
  serviceFeeMin?: unknown
}

/**
 * One per-event "what does a buyer pay for a $X ticket" function, per channel.
 * Every price a buyer sees before checkout — the ticket list, a section's
 * price range on the seat map, a seat tooltip, a table picker — must come
 * from this, so they all agree with the charge.
 *
 * Storefront: the fee rows marked for the storefront, else the legacy
 * per-event service fee; tax unless the event turns it off there.
 * Box office: the fee rows marked for the box office, else the legacy fee
 * only when applyFeesAtBoxOffice; tax only when applyTaxAtBoxOffice.
 * The per-order fixed fee is never folded into a per-ticket price.
 */
/**
 * `unitTickets` is how many TICKETS the base price covers. It is 1 for an
 * ordinary seat, and the table's seat count for a per-table section, where the
 * admin-configured price is the whole table.
 *
 * It matters because a `perTicket` fixed fee has to be charged once per ticket.
 * Freedom Fighter's live Freedom 43 carries Processing 3% per order plus a
 * $1.50 Service Fee with perTicket = TRUE, so a 6-seat $900 table is
 * 900 + 27 + (1.50 x 6) = $936.00 -- which is what the cart and checkout
 * charge. This pricer defaulted the count to 1 and advertised the table at
 * $928.50, one Service Fee instead of six, so the section header and the cart
 * disagreed by $7.50 on a live on-sale.
 */
export function makeAllInPricer(event: AllInPricingEvent, channel: SalesChannel = 'storefront'): (basePrice: number, unitTickets?: number) => number {
  const fees = event.eventFees || []
  const hasDetailedFees = fees.length > 0
  const taxRate = event.taxRate != null ? Number(event.taxRate) : 0.08
  const feeItems = hasDetailedFees ? extractEventFees(fees) : []
  const feeConfig = hasDetailedFees ? null : extractFeeConfig(event)
  if (channel === 'boxoffice') {
    const applyTax = event.applyTaxAtBoxOffice === true
    return (basePrice: number, unitTickets = 1) => {
      if (basePrice <= 0) return 0
      const count = Math.max(1, Math.floor(unitTickets))
      if (hasDetailedFees) {
        const { totalFees, tax } = calculateDetailedFees(basePrice, feeItems, taxRate, 'boxoffice', applyTax, count)
        return Math.round((basePrice + totalFees + tax) * 100) / 100
      }
      if (event.applyFeesAtBoxOffice === true) {
        const { serviceFee, tax } = calculateFees(basePrice, { ...feeConfig, serviceFeeFixed: 0 })
        return Math.round((basePrice + serviceFee + (applyTax ? tax : 0)) * 100) / 100
      }
      return basePrice
    }
  }
  const applyTax = event.applyTaxAtStorefront !== false
  return (basePrice: number, unitTickets = 1) => {
    if (basePrice <= 0) return 0
    const count = Math.max(1, Math.floor(unitTickets))
    if (hasDetailedFees) {
      // Not calculateAllInPriceDetailed: that one is per-UNIT and cannot carry
      // a ticket count, which is exactly the bug being fixed here.
      const { totalFees, tax } = calculateDetailedFees(basePrice, feeItems, taxRate, 'storefront', applyTax, count)
      return Math.round((basePrice + totalFees + tax) * 100) / 100
    }
    // The legacy config has no per-ticket concept: its fixed fee is per order
    // and its percentage scales with the amount, so the count cannot apply.
    return calculateAllInPrice(basePrice, feeConfig)
  }
}

